import { IDocumentFolderModel } from '@api/models';
import { Button } from '@hyperflake/react-ui-library';
import { Drawer } from '@library/drawer';
import { Label } from '@library/forms';
import { useMutation } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import FolderDestinationPicker from '../FolderDestinationPicker/FolderDestinationPicker';

interface MoveItemDrawerProps {
    show: boolean;
    onClose: () => void;
    refetchAll: () => void;

    title: string;
    itemName: string;

    folders: IDocumentFolderModel[];
    initialLocation: string | null;

    onMove: (destinationParentId: string | null) => Promise<any>;

    successMessage: string;

    currentLocationParentId?: string | null;
    initialBrowseParentId?: string | null;
    highlightFolderId?: string | null;
    highlightLabel?: string;
    excludeFolderIds?: Set<string>;
}

const MoveItemDrawer: React.FC<MoveItemDrawerProps> = ({
    show,
    onClose,
    refetchAll,
    title,
    itemName,
    folders,
    initialLocation,
    onMove,
    successMessage,
    currentLocationParentId = null,
    initialBrowseParentId,
    highlightFolderId,
    highlightLabel,
    excludeFolderIds,
}) => {
    const [destinationParentId, setDestinationParentId] = useState<string | null>(initialLocation);

    useEffect(() => {
        setDestinationParentId(initialLocation);
    }, [initialLocation, show]);

    const moveMutation = useMutation({
        mutationFn: async () => onMove(destinationParentId),
        onSuccess: () => {
            toast.success(successMessage);
            refetchAll();
            onClose();
        },
    });

    return (
        <Drawer open={show} onClose={onClose}>
            <div className="flex flex-col h-[calc(100dvh-3rem)]">
                <h4>
                    {title} - {itemName}
                </h4>

                <Formik
                    enableReinitialize
                    initialValues={{}}
                    onSubmit={() => {
                        moveMutation.mutate();
                    }}
                >
                    {() => (
                        <Form className="flex flex-col flex-1 gap-4">
                            <Label>Move to</Label>

                            <FolderDestinationPicker
                                folders={folders}
                                value={destinationParentId}
                                onChange={setDestinationParentId}
                                currentLocationParentId={currentLocationParentId}
                                initialBrowseParentId={initialBrowseParentId}
                                highlightFolderId={highlightFolderId}
                                highlightLabel={highlightLabel}
                                excludeFolderIds={excludeFolderIds}
                            />

                            <div className="mt-auto">
                                <div className="flex gap-4 mt-2 pb-4">
                                    <Button type="button" color="secondary" className="font-medium" onClick={onClose}>
                                        Cancel
                                    </Button>

                                    <Button type="submit" disabled={moveMutation.isPending}>
                                        {moveMutation.isPending ? 'Moving...' : 'Move'}
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </Drawer>
    );
};

export default MoveItemDrawer;
